import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Calendar, Clock, Phone, Mail, MapPin, Instagram, Facebook, ArrowRight, Users, ChevronLeft } from 'lucide-react';
import ApiService from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { isWorkingDay } from '../utils/teamMemberSchedule';

interface Service {
  id: number;
  name: string;
  price: number;
  duration: number;
  category: string;
  description: string;
  isActive: boolean;
  displayOrder?: number;
}

interface Category {
  id: number;
  name: string;
  displayName: string;
  icon: string;
  description?: string;
  isActive: boolean;
  displayOrder: number;
  backgroundImage?: string;
}

interface Package {
  id: number;
  name: string;
  description: string;
  price: number;
  duration: number;
  services: Service[];
  serviceIds?: number[]; // Opsiyonel serviceIds alanı
  isActive: boolean;
}

interface TeamMember {
  id: number;
  name: string;
  specialization: string;
  imageUrl?: string;
  sortOrder: number;
  isActive: boolean;
  workingDays?: number[] | string;
  sundayStartHour?: number;
}

interface TimeSlot {
  time: string;
  available: boolean;
  capacity?: number;
  bookedCount?: number;
  currentCount?: number;
  maxCapacity?: number;
  remainingSlots?: number;
  duration?: number;
  wouldEndAt?: number;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const Contact: React.FC = () => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    selectedServices: [] as string[],
    selectedPackages: [] as string[],
    teamMemberId: '',
    date: '',
    time: '',
    notes: '',
    showServicesDropdown: false,
    kvkkAccepted: false,
    customDuration: '' // Özel süre alanı eklendi
  });

  // Seçilen ekip üyesini takip etmek için
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
  
  // Fotoğraf modal'ı için state
  const [showImageModal, setShowImageModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  
  const [services, setServices] = useState<Service[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [availableDates, setAvailableDates] = useState<Array<{date: string, available: boolean, isSunday: boolean}>>([]);
  const [teamMemberAvailability, setTeamMemberAvailability] = useState<{[key: string]: {date: string, time: string}}>({});
  const [loading, setLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadServices();
    loadPackages();
    loadCategories();
    loadHomeSettings();
    
    // Sayfa yüklendiğinde en üste scroll yap
    window.scrollTo(0, 0);
  }, []);

  // Tooltip'i 3 saniye sonra gizle
  useEffect(() => {
    if (selectedTeamMember) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [selectedTeamMember]);

  useEffect(() => {
    if (location.state && services.length > 0) {
      if (location.state.selectedService) {
        const selectedService = services.find(s => s.id === location.state.selectedService);
        if (selectedService) {
          setFormData(prev => ({ 
            ...prev, 
            service: selectedService.name,
            selectedServices: [selectedService.id.toString()]
          }));
        }
      } else if (location.state.selectedServices && Array.isArray(location.state.selectedServices)) {
        // Birden çok hizmet seçilmişse
        const selectedServices = services.filter(s => location.state.selectedServices.includes(s.id));
        if (selectedServices.length > 0) {
          const serviceNames = selectedServices.map(s => s.name).join(', ');
          const serviceIds = selectedServices.map(s => s.id.toString());
          setFormData(prev => ({ 
            ...prev, 
            service: serviceNames,
            selectedServices: serviceIds
          }));
        }
      } else if (location.state.selectedPackage) {
        setFormData(prev => ({ 
          ...prev, 
          service: `Package: ${location.state.selectedPackage}`,
          selectedServices: []
        }));
      }
    }
  }, [location.state, services]);

  useEffect(() => {
    if (formData.date && formData.teamMemberId) {
      loadAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [formData.date, formData.teamMemberId]);

  // Ekip üyesi veya hizmet seçimi değiştiğinde müsait tarihleri yeniden yükle
  useEffect(() => {
    if (currentStep === 3 && formData.teamMemberId) {
      loadAvailableDates();
    }
  }, [formData.teamMemberId, formData.selectedServices, formData.selectedPackages, currentStep]);



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.services-dropdown')) {
        setFormData(prev => ({ ...prev, showServicesDropdown: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadServices = async () => {
    if (servicesLoading) return;
    
    try {
      setServicesLoading(true);
      const response = await ApiService.getServices();
      
      let servicesData = [];
      if (response && Array.isArray(response)) {
        servicesData = response;
      } else if (response && response.services && Array.isArray(response.services)) {
        servicesData = response.services;
      } else if (response && Array.isArray(response.data)) {
        servicesData = response.data;
      }
      
      const filteredServices = servicesData.filter((service: Service) => service.isActive !== false);
      
      // Debug: Sadece geliştirme modunda log göster
      if (process.env.NODE_ENV === 'development') {
        console.log('Services loaded:', {
          total: servicesData.length,
          active: filteredServices.length,
          inactive: servicesData.length - filteredServices.length
        });
        
        // Kategori bazında hizmet sayılarını logla
        const servicesByCategory = servicesData.reduce((acc: Record<string, { total: number, active: number, inactive: number }>, service: any) => {
          if (!acc[service.category]) {
            acc[service.category] = { total: 0, active: 0, inactive: 0 };
          }
          acc[service.category].total++;
          if (service.isActive !== false) {
            acc[service.category].active++;
          } else {
            acc[service.category].inactive++;
          }
          return acc;
        }, {} as Record<string, { total: number, active: number, inactive: number }>);
        
        console.log('Services by category:', servicesByCategory);
      }
      setServices(filteredServices);
    } catch (error) {
      console.error('Services load error:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await ApiService.getPackages();
      
      let packagesData = [];
      if (response && Array.isArray(response)) {
        packagesData = response;
      } else if (response && response.packages && Array.isArray(response.packages)) {
        packagesData = response.packages;
      } else if (response && Array.isArray(response.data)) {
        packagesData = response.data;
      }
      
      const filteredPackages = packagesData.filter((pkg: Package) => pkg.isActive !== false);
      setPackages(filteredPackages);
    } catch (error) {
      console.error('Packages load error:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await ApiService.getCategories();
      const categoriesData = response.categories || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Categories load error:', error);
    }
  };

  const loadTeamMembers = async (categoryIds?: number[]) => {
    if (teamMembersLoading) return;
    
    try {
      setTeamMembersLoading(true);
      
      // Eğer kategori ID'leri belirtilmişse, sadece o kategorilerde çalışanları getir
      let response;
      if (categoryIds && categoryIds.length > 0) {
        // Birden fazla kategori için her birini kontrol et
        const allTeamMembers = [];
        for (const categoryId of categoryIds) {
          const categoryResponse = await ApiService.getTeamMembersByCategory(categoryId);
          if (categoryResponse && categoryResponse.teamMembers) {
            allTeamMembers.push(...categoryResponse.teamMembers);
          }
        }
        
        // Tekrarlanan çalışanları kaldır
        const uniqueTeamMembers = allTeamMembers.filter((member, index, self) => 
          index === self.findIndex(m => m.id === member.id)
        );
        
        response = { teamMembers: uniqueTeamMembers };
      } else {
        // Tüm çalışanları getir
        response = await ApiService.getTeamMembers();
      }
      
      setTeamMembers(response.teamMembers || []);
    } catch (error) {
      console.error('Team members load error:', error);
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        console.log('HomeSettings loaded:', response.settings);
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  // Seçilen hizmetlerin ve paketlerin toplam süresini hesapla
  const calculateTotalDuration = useCallback(() => {
    let totalDuration = 0;
    
    // Seçilen hizmetlerin sürelerini topla
    formData.selectedServices.forEach(serviceId => {
      const service = services.find(s => s.id.toString() === serviceId);
      if (service && service.duration) {
        totalDuration += service.duration;
      }
    });
    
    // Seçilen paketlerin sürelerini topla
    formData.selectedPackages.forEach(packageId => {
      const pkg = packages.find(p => p.id.toString() === packageId);
      if (pkg && pkg.duration) {
        totalDuration += pkg.duration;
      } else if (pkg && pkg.services && Array.isArray(pkg.services)) {
        // Eğer paket süresi yoksa, paket içindeki hizmetlerin sürelerini topla
        pkg.services.forEach((service: any) => {
          if (service && service.duration) {
            totalDuration += service.duration;
          }
        });
      }
    });
    
    console.log('Duration calculation:', {
      selectedServices: formData.selectedServices,
      selectedPackages: formData.selectedPackages,
      totalDuration
    });
    
    return totalDuration;
  }, [formData.selectedServices, formData.selectedPackages, services, packages]);

  const loadAvailableSlots = async () => {
    try {
      console.log('Loading available slots for:', formData.date, 'team member:', formData.teamMemberId, 'services:', formData.selectedServices, 'packages:', formData.selectedPackages);
      
      // Seçilen hizmetlerin toplam süresini hesapla
      const totalDuration = calculateTotalDuration();
      console.log('Total duration calculated:', totalDuration);
      
      const response = await ApiService.getAvailableSlots(formData.date, formData.teamMemberId, formData.selectedServices, formData.selectedPackages, totalDuration);
      console.log('Available slots response:', response);
      if (response && response.availableSlots) {
        // Backend'den gelen slot'ları kullan (zaten sadece aktif randevular dikkate alınmış)
        setAvailableSlots(response.availableSlots);
      }
    } catch (error) {
      console.error('Available slots load error:', error);
      setAvailableSlots([]);
    }
  };

  const loadAvailableDates = async () => {
    try {
      console.log('Loading available dates for team member:', formData.teamMemberId, 'services:', formData.selectedServices, 'packages:', formData.selectedPackages);
      
      // Seçilen hizmetlerin toplam süresini hesapla
      const totalDuration = calculateTotalDuration();
      console.log('Total duration for dates:', totalDuration);
      
      const response = await ApiService.getAvailableDates(formData.teamMemberId, formData.selectedServices, formData.selectedPackages, totalDuration);
      console.log('Available dates response:', response);
      if (response && response.availableDates) {
        setAvailableDates(response.availableDates);
      }
    } catch (error) {
      console.error('Available dates load error:', error);
      setAvailableDates([]);
    }
  };

  const loadTeamMemberAvailability = useCallback(async () => {
    try {
      const totalDuration = calculateTotalDuration();
      console.log('Loading team member availability for duration:', totalDuration);
      
      const availabilityData: {[key: string]: {date: string, time: string}} = {};
      
      // Bugünün tarihini kontrol et - Türkiye saati
      const currentDate = new Date();
      const turkeyTime = new Date(currentDate.getTime() + (3 * 60 * 60 * 1000));
      const todayStr = turkeyTime.toISOString().split('T')[0];
      console.log('Frontend today (Turkey time):', todayStr);
      
      // Her ekip üyesi için müsait tarih ve saat bul
      for (const member of teamMembers) {
        try {
          console.log(`Loading availability for member ${member.id} (${member.name})`);
          
          const datesResponse = await ApiService.getAvailableDates(member.id.toString(), formData.selectedServices, formData.selectedPackages, totalDuration);
          
          if (datesResponse && datesResponse.availableDates) {
            console.log(`Available dates for member ${member.id} (${member.name}):`, datesResponse.availableDates);
            console.log(`Today string: ${todayStr}`);
            
            // Müsait tarihleri filtrele - sadece geçmiş tarihleri çıkar
            const availableDates = datesResponse.availableDates.filter((d: any) => {
              // Sunucu her günün müsaitliğini bayrakla söylüyor; yalnız gerçekten
              // müsait günleri gez — en yakın müsait tarih 30 günün neresindeyse bulunur.
              return d.date >= todayStr && d.available;
            });
            console.log(`Filtered available dates for member ${member.id} (${member.name}):`, availableDates);
            
            // İlk müsait tarihi bul (bugünden sonraki)
            const firstAvailableDate = availableDates[0];
            
            if (firstAvailableDate) {
              console.log(`First available date for member ${member.id} (${member.name}):`, firstAvailableDate);
              
              // Tüm müsait tarihleri kontrol et, ilk müsait saati bulana kadar devam et
              for (const availableDate of availableDates.slice(0, 5)) { // en fazla 5 gün dene; sınırsız tarama API limitini yiyordu
                console.log(`Checking slots for date ${availableDate.date} for member ${member.id} (${member.name})`);
                
                // Bu tarih için müsait saatleri al
                const slotsResponse = await ApiService.getAvailableSlots(availableDate.date, member.id.toString(), formData.selectedServices, formData.selectedPackages, totalDuration);
                
                if (slotsResponse && slotsResponse.availableSlots) {
                  // İlk müsait saati bul
                  const firstAvailableSlot = slotsResponse.availableSlots.find((slot: any) => slot.available);
                  
                  if (firstAvailableSlot) {
                    availabilityData[member.id.toString()] = {
                      date: availableDate.date,
                      time: firstAvailableSlot.time
                    };
                    console.log(`Set availability for member ${member.id} (${member.name}):`, availabilityData[member.id.toString()]);
                    break; // İlk müsait tarih ve saat bulundu, döngüden çık
                  } else {
                    console.log(`No available slots found for member ${member.id} (${member.name}) on date ${availableDate.date}, checking next date...`);
                  }
                } else {
                  console.log(`No slots response for member ${member.id} (${member.name}) on date ${availableDate.date}, checking next date...`);
                }
              }
              
              // Eğer hiçbir tarihte müsait saat bulunamadıysa
              if (!availabilityData[member.id.toString()]) {
                console.log(`No available slots found for member ${member.id} (${member.name}) on any available date`);
              }
            } else {
              console.log(`No available dates found for member ${member.id} (${member.name})`);
            }
          } else {
            console.log(`No dates response for member ${member.id} (${member.name})`);
          }
        } catch (error) {
          console.error(`Error loading availability for team member ${member.id} (${member.name}):`, error);
          // Hata olsa bile diğer ekip üyelerini kontrol etmeye devam et
        }
        if (!availabilityData[member.id.toString()]) {
          // 30 günde müsait gün yok ya da istek hatası: boş işaret bırak ki kart
          // "Müsaitlik kontrol ediliyor..." yazısında sonsuza dek asılı kalmasın.
          availabilityData[member.id.toString()] = { date: '', time: '' };
        }
      }
      
      setTeamMemberAvailability(availabilityData);
      console.log('Final team member availability loaded:', availabilityData);
    } catch (error) {
      console.error('Team member availability load error:', error);
      setTeamMemberAvailability({});
    }
  }, [formData.selectedServices, formData.selectedPackages, teamMembers, calculateTotalDuration]);

  // Manuel süre ile müsaitlik yükleme fonksiyonu
  const loadTeamMemberAvailabilityWithDuration = useCallback(async (totalDuration: number) => {
    try {
      console.log('Loading team member availability for manual duration:', totalDuration);
      
      const availabilityData: {[key: string]: {date: string, time: string}} = {};
      
      // Bugünün tarihini kontrol et - Türkiye saati
      const currentDate = new Date();
      const turkeyTime = new Date(currentDate.getTime() + (3 * 60 * 60 * 1000));
      const todayStr = turkeyTime.toISOString().split('T')[0];
      console.log('Frontend today (Turkey time):', todayStr);
      
      // Her ekip üyesi için müsait tarih ve saat bul
      for (const member of teamMembers) {
        try {
          console.log(`Loading availability for member ${member.id} (${member.name}) with duration ${totalDuration}`);
          
          const datesResponse = await ApiService.getAvailableDates(member.id.toString(), formData.selectedServices, formData.selectedPackages, totalDuration);
          
          if (datesResponse && datesResponse.availableDates) {
            console.log(`Available dates for member ${member.id} (${member.name}):`, datesResponse.availableDates);
            
            // Müsait tarihleri filtrele - sadece geçmiş tarihleri çıkar
            const availableDates = datesResponse.availableDates.filter((d: any) => {
              // Sunucu her günün müsaitliğini bayrakla söylüyor; yalnız gerçekten
              // müsait günleri gez — en yakın müsait tarih 30 günün neresindeyse bulunur.
              return d.date >= todayStr && d.available;
            });
            console.log(`Filtered available dates for member ${member.id} (${member.name}):`, availableDates);
            
            // İlk müsait tarihi bul (bugünden sonraki)
            const firstAvailableDate = availableDates[0];
            
            if (firstAvailableDate) {
              console.log(`First available date for member ${member.id} (${member.name}):`, firstAvailableDate);
              
              // Tüm müsait tarihleri kontrol et, ilk müsait saati bulana kadar devam et
              for (const availableDate of availableDates.slice(0, 5)) { // en fazla 5 gün dene; sınırsız tarama API limitini yiyordu
                console.log(`Checking slots for date ${availableDate.date} for member ${member.id} (${member.name})`);
                
                // Bu tarih için müsait saatleri al
                const slotsResponse = await ApiService.getAvailableSlots(availableDate.date, member.id.toString(), formData.selectedServices, formData.selectedPackages, totalDuration);
                
                if (slotsResponse && slotsResponse.availableSlots) {
                  // İlk müsait saati bul
                  const firstAvailableSlot = slotsResponse.availableSlots.find((slot: any) => slot.available);
                  
                  if (firstAvailableSlot) {
                    availabilityData[member.id.toString()] = {
                      date: availableDate.date,
                      time: firstAvailableSlot.time
                    };
                    console.log(`Set availability for member ${member.id} (${member.name}):`, availabilityData[member.id.toString()]);
                    break; // İlk müsait tarih ve saat bulundu, döngüden çık
                  } else {
                    console.log(`No available slots found for member ${member.id} (${member.name}) on date ${availableDate.date}, checking next date...`);
                  }
                } else {
                  console.log(`No slots response for member ${member.id} (${member.name}) on date ${availableDate.date}, checking next date...`);
                }
              }
              
              // Eğer hiçbir tarihte müsait saat bulunamadıysa
              if (!availabilityData[member.id.toString()]) {
                console.log(`No available slots found for member ${member.id} (${member.name}) on any available date`);
              }
            } else {
              console.log(`No available dates found for member ${member.id} (${member.name})`);
            }
          } else {
            console.log(`No dates response for member ${member.id} (${member.name})`);
          }
        } catch (error) {
          console.error(`Error loading availability for team member ${member.id} (${member.name}):`, error);
          // Hata olsa bile diğer ekip üyelerini kontrol etmeye devam et
        }
        if (!availabilityData[member.id.toString()]) {
          // 30 günde müsait gün yok ya da istek hatası: boş işaret bırak ki kart
          // "Müsaitlik kontrol ediliyor..." yazısında sonsuza dek asılı kalmasın.
          availabilityData[member.id.toString()] = { date: '', time: '' };
        }
      }
      
      setTeamMemberAvailability(availabilityData);
      console.log('Final team member availability loaded with manual duration:', availabilityData);
    } catch (error) {
      console.error('Team member availability load error:', error);
      setTeamMemberAvailability({});
    }
  }, [formData.selectedServices, formData.selectedPackages, teamMembers]);

  // 2. adımda hizmet/paket değiştiğinde ekip üyesi müsaitliklerini yeniden yükle
  useEffect(() => {
    if (currentStep === 2 && teamMembers.length > 0 && (formData.selectedServices.length > 0 || formData.selectedPackages.length > 0)) {
      loadTeamMemberAvailability();
    }
  }, [loadTeamMemberAvailability, currentStep, teamMembers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Tarih seçimi için özel kontrol
    if (name === 'date') {
      console.log('Selected date:', value);
      console.log('Is date disabled:', isDateDisabled(value));
      
      if (isDateDisabled(value)) {
        setMessage({ type: 'error', text: getDateValidationError(value) || 'Seçilen tarih müsait değil.' });
        return;
      }
      setMessage({ type: '', text: '' }); // Hata mesajını temizle
    }
    
      // Ekip üyesi seçimi için özel kontrol (artık ikinci adımda yapılıyor)
  if (name === 'teamMemberId') {
    const selectedMember = teamMembers.find(member => member.id.toString() === value);
    setSelectedTeamMember(selectedMember || null);
  }
    

    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId: string) => {
    const serviceToToggle = services.find(s => s.id.toString() === serviceId);
    if (!serviceToToggle) return;

    setFormData(prev => {
      const isSelected = prev.selectedServices.includes(serviceId);
      let newSelectedServices: string[];
      
      if (isSelected) {
        // Hizmeti kaldır
        newSelectedServices = prev.selectedServices.filter(id => id !== serviceId);
      } else {
        // Yeni hizmetin kategorisini kontrol et
        const currentSelectedServices = services.filter(s => prev.selectedServices.includes(s.id.toString()));
        const currentSelectedPackages = packages.filter(p => prev.selectedPackages.includes(p.id.toString()));
        
        // Mevcut seçili hizmetlerin kategorilerini topla
        const currentCategories = new Set<string>();
        currentSelectedServices.forEach(s => {
          if (s.category) currentCategories.add(s.category);
        });
        
        // Mevcut seçili paketlerin içindeki hizmetlerin kategorilerini topla
        currentSelectedPackages.forEach(pkg => {
          if (pkg.services && Array.isArray(pkg.services)) {
            pkg.services.forEach((service: any) => {
              if (service && service.category) currentCategories.add(service.category);
            });
          }
        });
        
        // Eğer yeni hizmet farklı bir kategorideyse ve zaten başka kategoriler seçiliyse uyarı ver
        if (serviceToToggle.category && currentCategories.size > 0 && !currentCategories.has(serviceToToggle.category)) {
          setMessage({ 
            type: 'error', 
            text: `Farklı kategorilerden hizmet seçemezsiniz. Önce mevcut seçimleri kaldırın. (Mevcut: ${Array.from(currentCategories).join(', ')}, Yeni: ${serviceToToggle.category})` 
          });
          return prev; // Değişiklik yapma
        }
        
        // Hizmeti ekle
        newSelectedServices = [...prev.selectedServices, serviceId];
      }
      
      // Seçilen hizmetlerin ve paketlerin isimlerini güncelle
      const selectedServices = services.filter(s => newSelectedServices.includes(s.id.toString()));
      const selectedPackages = packages.filter(p => prev.selectedPackages.includes(p.id.toString()));
      
      const serviceNames = selectedServices.map(s => s.name);
      const packageNames = selectedPackages.map(p => p.name);
      const allNames = [...serviceNames, ...packageNames];
      
      // Hata mesajını temizle
      setMessage({ type: '', text: '' });
      
      const newFormData = {
        ...prev,
        service: allNames.join(', '),
        selectedServices: newSelectedServices
      };

      // Müsaitlik yenilemesi yukarıdaki tek effect'ten gelir (selectedServices bağımlılığı);
      // buradaki ikinci manuel yükleme 60/dk API limitini tüketiyordu.
      
      return newFormData;
    });
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handlePackageToggle = (packageId: string) => {
    const packageToToggle = packages.find(p => p.id.toString() === packageId);
    if (!packageToToggle) return;

    setFormData(prev => {
      const isSelected = prev.selectedPackages.includes(packageId);
      let newSelectedPackages: string[];
      
      if (isSelected) {
        // Paketi kaldır
        newSelectedPackages = prev.selectedPackages.filter(id => id !== packageId);
      } else {
        // Yeni paketin kategorisini kontrol et
        const currentSelectedServices = services.filter(s => prev.selectedServices.includes(s.id.toString()));
        const currentSelectedPackages = packages.filter(p => prev.selectedPackages.includes(p.id.toString()));
        
        // Mevcut seçili hizmetlerin kategorilerini topla
        const currentCategories = new Set<string>();
        currentSelectedServices.forEach(s => {
          if (s.category) currentCategories.add(s.category);
        });
        
        // Mevcut seçili paketlerin içindeki hizmetlerin kategorilerini topla
        currentSelectedPackages.forEach(pkg => {
          if (pkg.services && Array.isArray(pkg.services)) {
            pkg.services.forEach((service: any) => {
              if (service && service.category) currentCategories.add(service.category);
            });
          }
        });
        
        // Yeni paketin içindeki hizmetlerin kategorilerini kontrol et
        const newPackageCategories = new Set<string>();
        if (packageToToggle.services && Array.isArray(packageToToggle.services)) {
          packageToToggle.services.forEach((service: any) => {
            if (service && service.category) newPackageCategories.add(service.category);
          });
        }
        
        // Eğer yeni paket farklı kategorilerde hizmetler içeriyorsa ve zaten başka kategoriler seçiliyse uyarı ver
        if (newPackageCategories.size > 0 && currentCategories.size > 0) {
          const hasCommonCategory = Array.from(newPackageCategories).some(cat => currentCategories.has(cat));
          if (!hasCommonCategory) {
            setMessage({ 
              type: 'error', 
              text: `Farklı kategorilerden hizmet seçemezsiniz. Önce mevcut seçimleri kaldırın. (Mevcut: ${Array.from(currentCategories).join(', ')}, Yeni Paket: ${Array.from(newPackageCategories).join(', ')})` 
            });
            return prev; // Değişiklik yapma
          }
        }
        
        // Paketi ekle
        newSelectedPackages = [...prev.selectedPackages, packageId];
      }
      
      // Seçilen hizmetlerin ve paketlerin isimlerini güncelle
      const selectedServices = services.filter(s => prev.selectedServices.includes(s.id.toString()));
      const selectedPackages = packages.filter(p => newSelectedPackages.includes(p.id.toString()));
      
      const serviceNames = selectedServices.map(s => s.name);
      const packageNames = selectedPackages.map(p => p.name);
      const allNames = [...serviceNames, ...packageNames];
      
      // Hata mesajını temizle
      setMessage({ type: '', text: '' });
      
      return {
        ...prev,
        service: allNames.join(', '),
        selectedPackages: newSelectedPackages
      };
    });

    // Paket seçimi değiştiğinde müsaitlikleri yeniden yükle
    setTimeout(() => {
      if (currentStep === 2 && teamMembers.length > 0) {
        loadTeamMemberAvailability();
      }
    }, 100);
  };

  // Hizmet ve paket seçimi değiştiğinde slot'ları yeniden yükle
  useEffect(() => {
    if (formData.date && formData.teamMemberId) {
      loadAvailableSlots();
    }
  }, [formData.selectedServices, formData.selectedPackages, formData.date, formData.teamMemberId]);

  // 2. aşamaya geçildiğinde çalışanları yükle
  useEffect(() => {
    if (currentStep === 2) {
      const selectedServiceIds = formData.selectedServices.map(id => parseInt(id));
      const selectedPackageIds = formData.selectedPackages.map(id => parseInt(id));
      
      // Seçilen hizmetlerin kategorilerini bul
      const selectedCategories = new Set<number>();
      
      // Hizmetlerin kategorilerini ekle
      selectedServiceIds.forEach(serviceId => {
        const service = services.find(s => s.id === serviceId);
        if (service && service.category) {
          const category = categories.find(c => c.name === service.category);
          if (category) {
            selectedCategories.add(category.id);
          }
        }
      });
      
      // Paketlerin içindeki hizmetlerin kategorilerini ekle
      selectedPackageIds.forEach(packageId => {
        const pkg = packages.find(p => p.id === packageId);
        if (pkg && pkg.services && Array.isArray(pkg.services)) {
          pkg.services.forEach((service: any) => {
            if (service && service.category) {
              const category = categories.find(c => c.name === service.category);
              if (category) {
                selectedCategories.add(category.id);
              }
            }
          });
        }
      });
      
      // Kategorilere göre çalışanları yükle
      const categoryIds = Array.from(selectedCategories);
      if (categoryIds.length > 0) {
        loadTeamMembers(categoryIds);
      } else {
        // Hiç kategori seçilmemişse tüm çalışanları getir
        loadTeamMembers();
      }
    }
  }, [currentStep, formData.selectedServices, formData.selectedPackages, services, packages, categories]);

  // 3. aşamaya geçildiğinde müsait tarihleri yükle ve otomatik tarih seç
  useEffect(() => {
    if (currentStep === 3) {
      // Müsait tarihleri yükle
      if (formData.teamMemberId) {
        loadAvailableDates();
      }
      
      // Eğer tarih seçilmemişse seçili ekip üyesinin müsait tarihini kullan
      if (!formData.date && formData.teamMemberId) {
        const availability = teamMemberAvailability[formData.teamMemberId];
        if (availability?.date) {
          setFormData(prev => ({ ...prev, date: availability.date }));
        }
      }
    }
  }, [currentStep, formData.teamMemberId]);

  // Hizmet seçimi değiştiğinde çalışanları filtrele (3. aşamada)
  useEffect(() => {
    if (currentStep === 3) {
      const selectedServiceIds = formData.selectedServices.map(id => parseInt(id));
      const selectedPackageIds = formData.selectedPackages.map(id => parseInt(id));
      
      // Seçilen hizmetlerin kategorilerini bul
      const selectedCategories = new Set<number>();
      
      // Hizmetlerin kategorilerini ekle
      selectedServiceIds.forEach(serviceId => {
        const service = services.find(s => s.id === serviceId);
        if (service && service.category) {
          const category = categories.find(c => c.name === service.category);
          if (category) {
            selectedCategories.add(category.id);
          }
        }
      });
      
      // Paketlerin içindeki hizmetlerin kategorilerini ekle
      selectedPackageIds.forEach(packageId => {
        const pkg = packages.find(p => p.id === packageId);
        if (pkg && pkg.services && Array.isArray(pkg.services)) {
          pkg.services.forEach((service: any) => {
            if (service && service.category) {
              const category = categories.find(c => c.name === service.category);
              if (category) {
                selectedCategories.add(category.id);
              }
            }
          });
        }
      });
      
      // Kategorilere göre çalışanları yükle
      const categoryIds = Array.from(selectedCategories);
      if (categoryIds.length > 0) {
        loadTeamMembers(categoryIds);
      } else {
        // Hiç kategori seçilmemişse tüm çalışanları getir
        loadTeamMembers();
      }
    }
  }, [formData.selectedServices, formData.selectedPackages, currentStep, services, packages, categories]);

  // E-posta validasyonu
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Telefon validasyonu
  const isValidPhone = (phone: string) => {
    // Sadece rakamları al
    const digitsOnly = phone.replace(/\D/g, '');
    // 10 veya 11 haneli olmalı
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  };

  // Mobil cihaz kontrolü
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Form alanına scroll yapma fonksiyonu
  const scrollToForm = () => {
    if (isMobile()) {
      setTimeout(() => {
        const formElement = document.querySelector('.appointment-form');
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleNextStep = () => {
    // İlk aşama validasyonu
    if (currentStep === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        setMessage({ type: 'error', text: 'Lütfen tüm kişisel bilgileri doldurun.' });
        return;
      }
      
      // E-posta validasyonu
      if (!isValidEmail(formData.email)) {
        setMessage({ type: 'error', text: 'Lütfen geçerli bir e-posta adresi girin (örn: ornek@email.com).' });
        return;
      }
      
      // Telefon validasyonu
      if (!isValidPhone(formData.phone)) {
        setMessage({ type: 'error', text: 'Lütfen geçerli bir telefon numarası girin (10 veya 11 haneli).' });
        return;
      }
      
      if (!formData.kvkkAccepted) {
        setMessage({ type: 'error', text: 'KVKK Aydınlatma Metni\'ni kabul etmelisiniz.' });
        return;
      }
      setMessage({ type: '', text: '' });
      setCurrentStep(2);
      
      // Mobilde form alanına scroll yap
      scrollToForm();
    }
    // İkinci aşama validasyonu
    else if (currentStep === 2) {
      if (formData.selectedServices.length === 0 && formData.selectedPackages.length === 0) {
        setMessage({ type: 'error', text: 'Lütfen en az bir hizmet veya paket seçin.' });
        return;
      }
      if (!formData.teamMemberId) {
        setMessage({ type: 'error', text: 'Lütfen bir ekip üyesi seçin.' });
        return;
      }
      
      setMessage({ type: '', text: '' });
      setCurrentStep(3);
      
      // Mobilde form alanına scroll yap
      scrollToForm();
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setMessage({ type: '', text: '' });
      
      // Mobilde form alanına scroll yap
      scrollToForm();
    } else if (currentStep === 3) {
      setCurrentStep(2);
      setMessage({ type: '', text: '' });
      
      // Mobilde form alanına scroll yap
      scrollToForm();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || (formData.selectedServices.length === 0 && formData.selectedPackages.length === 0) || !formData.teamMemberId || !formData.date || !formData.time) {
      setMessage({ type: 'error', text: 'Lütfen tüm gerekli alanları doldurun.' });
      return;
    }
    
    // E-posta validasyonu
    if (!isValidEmail(formData.email)) {
      setMessage({ type: 'error', text: 'Lütfen geçerli bir e-posta adresi girin (örn: ornek@email.com).' });
      return;
    }
    
    // Telefon validasyonu
    if (!isValidPhone(formData.phone)) {
      setMessage({ type: 'error', text: 'Lütfen geçerli bir telefon numarası girin (10 veya 11 haneli).' });
      return;
    }
    
    // Tarih kontrolü
    if (isDateDisabled(formData.date)) {
      setMessage({ type: 'error', text: getDateValidationError(formData.date) || 'Seçilen tarih müsait değil.' });
      return;
    }
    
    // Saat kontrolü
    if (isTimeDisabled(formData.date, formData.time)) {
      setMessage({ type: 'error', text: 'Geçmiş saatlere randevu alamazsınız.' });
      return;
    }
    
    // İş saatleri kontrolü
    const totalDuration = calculateTotalDuration();
    if (!isWithinBusinessHours(formData.time, totalDuration)) {
      setMessage({ type: 'error', text: 'Seçilen süre iş saatleri dışında kalıyor.' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Seçilen hizmetlerin ve paketlerin ID'lerini birleştir
      const allServiceIds = [...formData.selectedServices];
      
      // Paketlerdeki hizmetleri de ekle
      formData.selectedPackages.forEach(packageId => {
        const pkg = packages.find(p => p.id.toString() === packageId);
        console.log('Processing package:', pkg);
        
        if (pkg) {
          // Farklı paket yapılarını kontrol et
          let packageServices: any[] = [];
          
          if (pkg.services && Array.isArray(pkg.services)) {
            packageServices = pkg.services;
          } else if (pkg.serviceIds && Array.isArray(pkg.serviceIds)) {
            // Eğer serviceIds varsa, bunları kullan
            packageServices = pkg.serviceIds.map((id: number) => ({ id }));
          }
          
          console.log('Package services to process:', packageServices);
          
          packageServices.forEach((service: any) => {
            console.log('Processing service:', service);
            // Güvenli ID kontrolü
            if (service && service.id && !allServiceIds.includes(service.id.toString())) {
              allServiceIds.push(service.id.toString());
            }
          });
          
          // Eğer paket hizmetleri bulunamadıysa, paket ID'sini service olarak ekle
          if (packageServices.length === 0) {
            console.log('No services found in package, adding package ID as service');
            allServiceIds.push(pkg.id.toString());
          }
        }
      });
      
      console.log('Final allServiceIds:', allServiceIds);

      const totalDuration = calculateTotalDuration();
      
      const appointmentData = {
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        serviceNames: [
          ...formData.selectedServices.map(serviceId => {
            const service = services.find(s => s.id.toString() === serviceId);
            return service ? service.name : '';
          }),
          ...formData.selectedPackages.map(packageId => {
            const pkg = packages.find(p => p.id.toString() === packageId);
            return pkg ? pkg.name : '';
          })
        ].filter(name => name),
        serviceIds: formData.selectedServices, // Sadece hizmet ID'leri
        packageIds: formData.selectedPackages, // Paket ID'leri ayrı
        teamMemberId: parseInt(formData.teamMemberId),
        date: formData.date,
        time: formData.time,
        notes: formData.notes,
        totalDuration: totalDuration, // Toplam süre dakika cinsinden
        customDuration: formData.customDuration ? parseInt(formData.customDuration) : null
      };

      // Önce müşteri oluştur veya mevcut müşteriyi bul
      let customerId;
      try {
        const customerResponse = await ApiService.createCustomer({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        });
        
        if (customerResponse && customerResponse.success) {
          customerId = customerResponse.customer.id;
        }
      } catch (error) {
        console.error('Customer creation error:', error);
        // Müşteri oluşturulamazsa devam et
      }

      const response = await ApiService.createAppointment(appointmentData);
      
      if (response && (response.success || response.appointment)) {
        // Başarılı randevu oluşturma pop-up'ını göster
        setShowSuccessModal(true);
        
        // Form verilerini temizle
                  setFormData({
            name: '',
            email: '',
            phone: '',
            service: '',
            selectedServices: [],
            selectedPackages: [],
            teamMemberId: '',
            date: '',
            time: '',
            notes: '',
            showServicesDropdown: false,
            kvkkAccepted: false,
            customDuration: ''
          });
          setSelectedTeamMember(null);
          setShowImageModal(false);
          setShowTooltip(true);
        setAvailableSlots([]);
        
        // İlk adıma yönlendir
        setCurrentStep(1);
      } else {
        setMessage({ type: 'error', text: response.message || 'Randevu oluşturulurken bir hata oluştu.' });
      }
    } catch (error: any) {
      console.error('Appointment creation error:', error);
      setMessage({ type: 'error', text: 'Randevu oluşturulurken bir hata oluştu: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const getTurkeyTodayStr = () => {
    const turkeyTime = new Date(Date.now() + 3 * 60 * 60 * 1000);
    return turkeyTime.toISOString().split('T')[0];
  };

  const getMaxBookingDate = () => {
    const turkeyTime = new Date(Date.now() + 3 * 60 * 60 * 1000);
    turkeyTime.setUTCFullYear(turkeyTime.getUTCFullYear() + 1);
    return turkeyTime.toISOString().split('T')[0];
  };

  const getDateValidationError = (dateString: string): string | null => {
    const todayStr = getTurkeyTodayStr();
    const maxDate = getMaxBookingDate();

    if (dateString < todayStr) {
      return 'Geçmiş tarih seçemezsiniz.';
    }

    if (dateString > maxDate) {
      return 'En fazla 1 yıl sonrasına randevu alabilirsiniz.';
    }

    if (formData.teamMemberId) {
      const member = teamMembers.find((m) => m.id.toString() === formData.teamMemberId);
      if (member && !isWorkingDay(member, dateString)) {
        return 'Seçilen ekip üyesi bu gün çalışmıyor.';
      }
    }

    return null;
  };

  const isDateDisabled = (dateString: string) => getDateValidationError(dateString) !== null;

  // Türkiye saati için bugünün tarihi
  const today = getTurkeyTodayStr();
  const maxBookingDate = getMaxBookingDate();
  
  // Geçmiş saat kontrolü (aynı gün için)
  const isTimeDisabled = (dateString: string, timeString: string) => {
    // Türkiye saati için UTC+3
    const currentDate = new Date();
    const turkeyTime = new Date(currentDate.getTime() + (3 * 60 * 60 * 1000));
    const todayStr = turkeyTime.toISOString().split('T')[0];
    
    // Eğer bugün değilse, saat kontrolü yapmaya gerek yok
    if (dateString !== todayStr) {
      return false;
    }
    
    // Türkiye saati için doğru saat hesaplaması
    const currentHour = turkeyTime.getUTCHours();
    const currentMinute = turkeyTime.getUTCMinutes();
    const currentTime = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    const selectedTime = timeString.substring(0, 5); // HH:MM format
    
    console.log('Time check:', {
      currentTime,
      selectedTime,
      isPastTime: selectedTime <= currentTime
    });
    
    return selectedTime <= currentTime;
  };

  // İş saatleri kontrolü
  const isWithinBusinessHours = (timeString: string, duration: number) => {
    const startTime = new Date(`2000-01-01T${timeString}`);
    const endTime = new Date(startTime.getTime() + duration * 60000);
    const businessEndTime = new Date('2000-01-01T18:00');
    
    return endTime <= businessEndTime;
  };

  return (
    <div className="text-white min-h-screen" style={{ backgroundColor: homeSettings?.backgroundColor || '#000000' }}>
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-light mb-4 md:mb-6 tracking-wider" style={{ fontFamily: 'FuturaLTPaneuropean', color: '#9e906d' }}>
            RANDEVU AL
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 md:mb-12 font-light max-w-3xl mx-auto px-2" style={{ color: '#000000' }}>
            "Beauty is power, and Sheva is your magic wand!"
          </p>
        </div>
      </section>

      {/* Progress Indicator */}
      <section className="py-4 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center space-x-4">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-white' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 1 ? 'bg-white text-black' : 'bg-gray-600 text-gray-300'
              }`}>
                1
              </div>
              <span className="ml-2 text-sm">Kişisel Bilgiler</span>
            </div>
            <div className={`w-8 h-1 ${currentStep >= 2 ? 'bg-white' : 'bg-gray-600'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-white' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 2 ? 'bg-white text-black' : 'bg-gray-600 text-gray-300'
              }`}>
                2
              </div>
              <span className="ml-2 text-sm">Hizmet & Ekip Üyesi</span>
            </div>
            <div className={`w-8 h-1 ${currentStep >= 3 ? 'bg-white' : 'bg-gray-600'}`}></div>
            <div className={`flex items-center ${currentStep >= 3 ? 'text-white' : 'text-gray-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= 3 ? 'bg-white text-black' : 'bg-gray-600 text-gray-300'
              }`}>
                3
              </div>
              <span className="ml-2 text-sm">Randevu Detayları</span>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Form Section */}
      <section className="py-8 md:py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Appointment Form - Compact Design */}
          <div className="appointment-form bg-[#9e906d] text-white p-6 md:p-8 rounded-lg shadow-lg">
            <h2 className="text-xl md:text-2xl font-medium mb-6 text-white">
              {currentStep === 1 ? 'Kişisel Bilgiler' : currentStep === 2 ? 'Hizmet ve Ekip Üyesi Seçimi' : 'Randevu Detayları'}
            </h2>
            
            {message.text && (
              <div className={`mb-4 p-3 border rounded ${
                message.type === 'success' 
                  ? 'border-green-300 text-green-700 bg-green-50' 
                  : 'border-red-300 text-red-700 bg-red-50'
              }`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Ad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  {/* KVKK Checkbox */}
                  <div className="mt-4">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="kvkkAccepted"
                        checked={formData.kvkkAccepted}
                        onChange={(e) => setFormData(prev => ({ ...prev, kvkkAccepted: e.target.checked }))}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        required
                      />
                      <div className="text-sm text-white">
                        <button
                          type="button"
                          onClick={() => setShowKvkkModal(true)}
                          className="font-medium text-blue-300 hover:text-blue-200 underline"
                        >
                          KVKK Aydınlatma Metni
                        </button>
                        'ni okudum ve{' '}
                        <span className="font-medium">Kişisel Verilerin Korunması Kanunu</span> kapsamında kişisel verilerimin işlenmesine izin veriyorum. *
                      </div>
                    </label>
                  </div>
                </>
              )}

              {/* Step 2: Service and Package Selection */}
              {currentStep === 2 && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-white">
                      Hizmetler ve Paketler *
                    </label>
                    <div className="relative services-dropdown">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, showServicesDropdown: !prev.showServicesDropdown }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {formData.selectedServices.length > 0 || formData.selectedPackages.length > 0 
                          ? `${formData.selectedServices.length + formData.selectedPackages.length} seçili`
                          : 'Hizmet veya paket seçin'
                        }
                      </button>
                      
                      {formData.showServicesDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[700px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                          <div className="p-2">
                            {/* Kategoriler */}
                            {categories.length > 0 && (
                              <div className="space-y-2">
                                {categories.filter(cat => cat.name !== 'packages').map((category) => {
                                  const categoryServices = services
                                    .filter(service => service.category === category.name)
                                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
                                  
                                  // Debug: Hizmet sayısını kontrol et (sadece geliştirme modunda)
                                  if (process.env.NODE_ENV === 'development') {
                                    const allCategoryServices = services.filter(service => service.category === category.name);
                                    const activeCategoryServices = allCategoryServices.filter(service => service.isActive !== false);
                                    if (allCategoryServices.length !== activeCategoryServices.length) {
                                      console.log(`Category ${category.name}: ${categoryServices.length} active services, ${allCategoryServices.length} total services`);
                                    }
                                  }
                                  const isExpanded = expandedCategories.has(category.id);
                                  
                                  // Mevcut seçili kategorileri belirle
                                  const currentSelectedServices = services.filter(s => formData.selectedServices.includes(s.id.toString()));
                                  const currentSelectedPackages = packages.filter(p => formData.selectedPackages.includes(p.id.toString()));
                                  
                                  const currentCategories = new Set<string>();
                                  currentSelectedServices.forEach(s => {
                                    if (s.category) currentCategories.add(s.category);
                                  });
                                  
                                  currentSelectedPackages.forEach(pkg => {
                                    if (pkg.services && Array.isArray(pkg.services)) {
                                      pkg.services.forEach((service: any) => {
                                        if (service && service.category) currentCategories.add(service.category);
                                      });
                                    }
                                  });
                                  
                                  // Bu kategori seçilebilir mi?
                                  const isCategorySelectable = currentCategories.size === 0 || currentCategories.has(category.name);
                                  
                                  return (
                                    <div key={`category-${category.id}`} className="border-b border-gray-100 pb-2">
                                      <button
                                        type="button"
                                        onClick={() => toggleCategory(category.id)}
                                        className={`w-full flex items-center justify-between p-2 rounded text-sm font-semibold transition-all duration-200 ${
                                          isCategorySelectable 
                                            ? 'hover:bg-gray-50 text-gray-700' 
                                            : 'text-gray-400 cursor-not-allowed'
                                        }`}
                                        disabled={!isCategorySelectable}
                                      >
                                        <div className="flex items-center">
                                          <span className="mr-2">{category.icon}</span>
                                          <span>{category.displayName}</span>
                                          {!isCategorySelectable && (
                                            <span className="ml-2 text-xs text-red-500">(Farklı kategori seçili)</span>
                                          )}
                                        </div>
                                        <div className="flex items-center">
                                          <span className="text-xs text-gray-500 mr-2">
                                            ({categoryServices.length} aktif hizmet)
                                          </span>
                                          <svg
                                            className={`w-4 h-4 transition-transform duration-200 ${
                                              isExpanded ? 'rotate-180' : ''
                                            }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                          </svg>
                                        </div>
                                      </button>
                                      
                                      {/* Kategori altındaki hizmetler - Animasyonlu */}
                                      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                        isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                                      }`}>
                                        <div className="space-y-1 pt-2 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                          {categoryServices.map((service) => (
                                            <div 
                                              key={`service-${service.id}`} 
                                              onClick={() => isCategorySelectable && handleServiceToggle(service.id.toString())}
                                              className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer transition-all duration-200 ${
                                                isCategorySelectable 
                                                  ? formData.selectedServices.includes(service.id.toString())
                                                    ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                                    : 'hover:bg-gray-50'
                                                  : 'opacity-50 cursor-not-allowed'
                                              }`}
                                            >
                                              <div className="flex-1">
                                                <span className={isCategorySelectable ? 'text-gray-800' : 'text-gray-400'}>
                                                  {service.name}
                                                </span>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                  {service.duration} dk • {service.price} ₺
                                                </div>
                                              </div>
                                              <div className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                                                formData.selectedServices.includes(service.id.toString())
                                                  ? 'bg-red-500 text-white'
                                                  : isCategorySelectable
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-300 text-gray-500'
                                              }`}>
                                                {formData.selectedServices.includes(service.id.toString()) ? '-' : '+'}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Paketler */}
                            {packages.length > 0 && (
                              <div className="border-t border-gray-200 pt-2">
                                <button
                                  type="button"
                                  onClick={() => toggleCategory(-1)} // Paketler için özel ID
                                  className="w-full flex items-center justify-between p-2 rounded text-sm font-semibold transition-all duration-200 hover:bg-gray-50 text-gray-700"
                                >
                                  <div className="flex items-center">
                                    <span className="mr-2">📦</span>
                                    <span>PAKETLER</span>
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-2">
                                      ({packages.length} paket)
                                    </span>
                                    <svg
                                      className={`w-4 h-4 transition-transform duration-200 ${
                                        expandedCategories.has(-1) ? 'rotate-180' : ''
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </div>
                                </button>
                                
                                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                  expandedCategories.has(-1) ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                                }`}>
                                  <div className="space-y-1 pt-2 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                  {packages.map((pkg) => {
                                    // Mevcut seçili kategorileri belirle
                                    const currentSelectedServices = services.filter(s => formData.selectedServices.includes(s.id.toString()));
                                    const currentSelectedPackages = packages.filter(p => formData.selectedPackages.includes(p.id.toString()));
                                    
                                    const currentCategories = new Set<string>();
                                    currentSelectedServices.forEach(s => {
                                      if (s.category) currentCategories.add(s.category);
                                    });
                                    
                                    currentSelectedPackages.forEach(selectedPkg => {
                                      if (selectedPkg.services && Array.isArray(selectedPkg.services)) {
                                        selectedPkg.services.forEach((service: any) => {
                                          if (service && service.category) currentCategories.add(service.category);
                                        });
                                      }
                                    });
                                    
                                    // Bu paketin kategorilerini belirle
                                    const packageCategories = new Set<string>();
                                    if (pkg.services && Array.isArray(pkg.services)) {
                                      pkg.services.forEach((service: any) => {
                                        if (service && service.category) packageCategories.add(service.category);
                                      });
                                    }
                                    
                                    // Bu paket seçilebilir mi?
                                    const isPackageSelectable = currentCategories.size === 0 || 
                                      Array.from(packageCategories).some(cat => currentCategories.has(cat));
                                    
                                    return (
                                      <div 
                                        key={`package-${pkg.id}`} 
                                        onClick={() => isPackageSelectable && handlePackageToggle(pkg.id.toString())}
                                        className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer transition-all duration-200 ${
                                          isPackageSelectable 
                                            ? formData.selectedPackages.includes(pkg.id.toString())
                                              ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
                                              : 'hover:bg-gray-50'
                                            : 'opacity-50 cursor-not-allowed'
                                        }`}
                                      >
                                        <div className="flex-1">
                                          <span className={isPackageSelectable ? 'text-gray-800' : 'text-gray-400'}>
                                            {pkg.name}
                                          </span>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {pkg.duration ? `${pkg.duration} dk` : 'Değişken süre'} • {pkg.price} ₺
                                          </div>
                                          {!isPackageSelectable && (
                                            <div className="text-xs text-red-500 mt-1">
                                              (Farklı kategori seçili)
                                            </div>
                                          )}
                                        </div>
                                        <div className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                                          formData.selectedPackages.includes(pkg.id.toString())
                                            ? 'bg-red-500 text-white'
                                            : isPackageSelectable
                                              ? 'bg-green-500 text-white'
                                              : 'bg-gray-300 text-gray-500'
                                        }`}>
                                          {formData.selectedPackages.includes(pkg.id.toString()) ? '-' : '+'}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {(formData.selectedServices.length > 0 || formData.selectedPackages.length > 0) && (
                      <div className="mt-2 p-2 bg-gray-100 rounded border border-gray-200">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-xs font-semibold text-gray-700">Seçilen Hizmetler:</div>
                          <div className="text-xs font-semibold text-gray-700">
                            Toplam Süre: {calculateTotalDuration()} dakika
                          </div>
                        </div>
                        
                        {/* Kategori Bilgisi */}
                        {(() => {
                          const selectedCategories = new Set<string>();
                          
                          // Seçili hizmetlerin kategorilerini topla
                          formData.selectedServices.forEach(serviceId => {
                            const service = services.find(s => s.id.toString() === serviceId);
                            if (service && service.category) {
                              selectedCategories.add(service.category);
                            }
                          });
                          
                          // Seçili paketlerin kategorilerini topla
                          formData.selectedPackages.forEach(packageId => {
                            const pkg = packages.find(p => p.id.toString() === packageId);
                            if (pkg && pkg.services && Array.isArray(pkg.services)) {
                              pkg.services.forEach((service: any) => {
                                if (service && service.category) {
                                  selectedCategories.add(service.category);
                                }
                              });
                            }
                          });
                          
                          if (selectedCategories.size > 0) {
                            return (
                              <div className="mb-2">
                                <div className="text-xs font-medium text-gray-600 mb-1">Kategori:</div>
                                <div className="flex flex-wrap gap-1">
                                  {Array.from(selectedCategories).map(categoryName => {
                                    const category = categories.find(c => c.name === categoryName);
                                    return (
                                      <span key={`category-${categoryName}`} className="bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                        {category ? category.displayName : categoryName}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        <div className="flex flex-wrap gap-1">
                          {formData.selectedServices.map((serviceId) => {
                            const service = services.find(s => s.id.toString() === serviceId);
                            return service ? (
                              <span key={`selected-service-${serviceId}`} className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                                {service.name} ({service.duration}dk • {service.price}₺)
                              </span>
                            ) : null;
                          })}
                          {formData.selectedPackages.map((packageId) => {
                            const pkg = packages.find(p => p.id.toString() === packageId);
                            return pkg ? (
                              <span key={`selected-package-${packageId}`} className="bg-purple-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                                {pkg.name} ({pkg.duration ? pkg.duration : 'Değişken'}dk • {pkg.price}₺)
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ekip Üyesi Seçimi */}
                  {(formData.selectedServices.length > 0 || formData.selectedPackages.length > 0) && (
                    <div className="mt-6">
                      <label className="block text-sm font-medium mb-2 text-white">
                        Ekip Üyesi Seçimi *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {teamMembers.map((member) => (
                          <div
                            key={member.id}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, teamMemberId: member.id.toString() }));
                              setSelectedTeamMember(member);
                              
                              // Seçilen ekip üyesinin müsait tarihini otomatik seç
                              const availability = teamMemberAvailability[member.id.toString()];
                              if (availability && availability.date) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  teamMemberId: member.id.toString(),
                                  date: availability.date,
                                  time: ''
                                }));
                                setMessage({ type: '', text: '' });
                              } else {
                                // Müsaitlik bilgisi yoksa tarih ve saati sıfırla
                                setFormData(prev => ({ 
                                  ...prev, 
                                  teamMemberId: member.id.toString(),
                                  date: '',
                                  time: ''
                                }));
                              }
                            }}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                              formData.teamMemberId === member.id.toString()
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 bg-white hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <img 
                                  src={member.imageUrl ? getImageUrl(member.imageUrl, 'team') : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo='} 
                                  alt={member.name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                                  }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                  {member.name}
                                </h3>
                                <p className="text-xs text-gray-500 truncate">
                                  {member.specialization}
                                </p>
                                <div className="mt-1">
                                  {teamMemberAvailability[member.id.toString()] ? (
                                    teamMemberAvailability[member.id.toString()].date ? (
                                      <div className="text-xs text-gray-600">
                                        {new Date(teamMemberAvailability[member.id.toString()].date).toLocaleDateString('tr-TR', {
                                          day: 'numeric',
                                          month: 'short'
                                        })} • {teamMemberAvailability[member.id.toString()].time}
                                      </div>
                                    ) : (
                                      <div className="text-xs text-gray-500">
                                        Yakın tarihte müsaitlik yok
                                      </div>
                                    )
                                  ) : (
                                    <div className="text-xs text-gray-500">
                                      Müsaitlik kontrol ediliyor...
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {teamMembers.length === 0 && (
                        <div className="text-center py-4 text-gray-500">
                          Seçilen hizmetler için uygun ekip üyesi bulunamadı.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Step 3: Appointment Details */}
              {currentStep === 3 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Seçilen Ekip Üyesi
                      </label>
                      {selectedTeamMember && (
                        <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-300">
                          <img 
                            src={selectedTeamMember.imageUrl ? getImageUrl(selectedTeamMember.imageUrl, 'team') : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo='} 
                            alt={selectedTeamMember.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:opacity-80 transition-all duration-200"
                            onClick={() => setShowImageModal(true)}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo=';
                            }}
                          />
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {selectedTeamMember.name}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {selectedTeamMember.specialization}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Tarih *
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        min={today}
                        max={maxBookingDate}
                        required
                        className="w-72 sm:w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onKeyDown={(e) => e.preventDefault()}
                      />
                      <div className="mt-1 text-xs text-gray-500">
                        En yakın müsait tarih otomatik seçilmiştir.
                      </div>
                      
                      {/* Müsait tarihler listesi */}

                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Saat *
                      </label>
                      {!formData.date || !formData.teamMemberId ? (
                        <select
                          name="time"
                          value={formData.time}
                          onChange={handleInputChange}
                          required
                          disabled
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Önce tarih ve ekip üyesi seçin</option>
                        </select>
                      ) : (
                        <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {availableSlots.map((slot, index) => (
                            <button
                              key={`${slot.time}-${index}`}
                              type="button"
                              onClick={() => {
                                if (slot.available) {
                                  if (isTimeDisabled(formData.date, slot.time)) {
                                    setMessage({ type: 'error', text: 'Geçmiş saatlere randevu alamazsınız.' });
                                    return;
                                  }
                                  
                                  // İş saatleri kontrolü
                                  const totalDuration = calculateTotalDuration();
                                  if (!isWithinBusinessHours(slot.time, totalDuration)) {
                                    setMessage({ type: 'error', text: 'Seçilen süre iş saatleri dışında kalıyor.' });
                                    return;
                                  }
                                  
                                  setFormData(prev => ({ ...prev, time: slot.time }));
                                  setMessage({ type: '', text: '' });
                                }
                              }}
                              disabled={!slot.available}
                              className={`px-1 py-1 text-xs rounded transition-all duration-200 ${
                                slot.available
                                  ? formData.time === slot.time
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-white hover:bg-gray-100 border border-gray-300 text-black'
                                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2 text-white">
                        Notlar
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center pt-4">
                {currentStep === 1 ? (
                  <div></div>
                ) : (
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className="flex items-center space-x-2 bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-all duration-200"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Geri</span>
                  </button>
                )}
                
                {currentStep === 1 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!formData.name || !formData.email || !formData.phone || !formData.kvkkAccepted}
                    className="flex items-center space-x-2 bg-blue-500 text-white py-2 px-6 rounded-md hover:bg-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Devam Et</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : currentStep === 2 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    disabled={formData.selectedServices.length === 0 && formData.selectedPackages.length === 0}
                    className="flex items-center space-x-2 bg-blue-500 text-white py-2 px-6 rounded-md hover:bg-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>Devam Et</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-blue-500 text-white py-2 px-6 rounded-md hover:bg-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Randevu Alınıyor...' : 'Randevu Al'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information Section - Below Form */}
      <section className="py-8 md:py-12 px-4" style={{ backgroundColor: homeSettings?.secondaryBackgroundColor || '#1f2937' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-8 md:mt-16">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-light mb-4 md:mb-6 tracking-wider">
                  BİZE ULAŞIN
                </h2>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 border border-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 md:w-5 md:h-5" style={{ color: homeSettings?.backgroundColor || '#000000' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-light">Telefon</h3>
                    <p className="text-sm md:text-base" style={{ color: homeSettings?.backgroundColor || '#000000' }}>0554 989 46 63</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 border border-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 md:w-5 md:h-5" style={{ color: homeSettings?.backgroundColor || '#000000' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-light">E-posta</h3>
                    <p className="text-sm md:text-base break-all" style={{ color: homeSettings?.backgroundColor || '#000000' }}>info@shevastudiokonya.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 border border-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 md:w-5 md:h-5" style={{ color: homeSettings?.backgroundColor || '#000000' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-light">Adres</h3>
                    <p className="text-sm md:text-base" style={{ color: homeSettings?.backgroundColor || '#000000' }}>Melikşah, Akkonak Sk. As Plaza D:1. Kat No:3, 42090 Meram/Konya</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 md:mb-6 tracking-wider">
                  ÇALIŞMA SAATLERİ
                </h3>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div className="flex justify-between text-sm md:text-base">
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>Salı - Cumartesi</span>
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>09:00 - 18:00</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>Pazar</span>
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>11:00 - 18:00</span>
                </div>
                <div className="flex justify-between text-sm md:text-base">
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>Pazartesi</span>
                  <span style={{ color: homeSettings?.backgroundColor || '#000000' }}>Kapalı</span>
                </div>
              </div>
            </div>

            <div className="space-y-6 md:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-light mb-4 md:mb-6 tracking-wider">
                  KONUM
                </h3>
              </div>

              <div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3149.6966998450084!2d32.463393800000006!3d37.8673866!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14d085558a4d25cd%3A0x8121d27392639317!2sAla%20Beaut%C3%A9%20Club!5e0!3m2!1str!2str!4v1755932846846!5m2!1str!2str"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sheva Studio Konum"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fotoğraf Modal */}
      {showImageModal && selectedTeamMember && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold z-10 bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              ×
            </button>
            <img 
              src={selectedTeamMember.imageUrl ? getImageUrl(selectedTeamMember.imageUrl, 'team') : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo='} 
              alt={selectedTeamMember.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5ZTkwNmQiLz4KPHN2ZyB4PSI4IiB5PSI4IiB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik0xMiAxMkMxNC4yMDkxIDEyIDE2IDEwLjIwOTEgMTYgOEMxNiA1Ljc5MDg2IDE0LjIwOTEgNCAxMiA0QzkuNzkwODYgNCA4IDUuNzkwODYgOCA4QzggMTAuMjA5MSA5Ljc5MDg2IDEyIDEyIDEyWiIgZmlsbD0id2hpdGUiLz4KPHBhdGggZD0iTTEyIDE0QzcuNTgxNzIgMTQgNCAxNy41ODE3IDQgMjJIMjBDMjAgMTcuNTgxNyAxNi40MTgzIDE0IDEyIDE0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cjwvc3ZnPgo=';
              }}
            />
          </div>
        </div>
      )}

      {/* KVKK Modal */}
      {showKvkkModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowKvkkModal(false)}
        >
          <div 
            className="bg-white text-black rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">KVKK Aydınlatma Metni</h3>
                <button
                  onClick={() => setShowKvkkModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              
              <div className="text-sm leading-relaxed space-y-4">
                <p>
                  <strong>1. Veri Sorumlusu:</strong><br />
                  Kişisel verileriniz, veri sorumlusu sıfatıyla Sheva Studio tarafından aşağıda açıklanan amaçlar kapsamında hukuka ve dürüstlük kurallarına uygun olarak işlenecektir.
                </p>
                
                <p>
                  <strong>2. İşlenen Kişisel Veriler:</strong><br />
                  • Kimlik bilgileri (ad, soyad)<br />
                  • İletişim bilgileri (telefon, e-posta)<br />
                  • Sağlık verileri (randevu bilgileri, hizmet tercihleri)<br />
                  • İşlem güvenliği verileri (IP adresi, çerez bilgileri)
                </p>
                
                <p>
                  <strong>3. Kişisel Verilerin İşlenme Amaçları:</strong><br />
                  • Randevu oluşturma ve yönetimi<br />
                  • Hizmet sunumu ve kalite kontrolü<br />
                  • Müşteri iletişimi ve bilgilendirme<br />
                  • Yasal yükümlülüklerin yerine getirilmesi<br />
                  • İş süreçlerinin yürütülmesi
                </p>
                
                <p>
                  <strong>4. Kişisel Verilerin Aktarılması:</strong><br />
                  Kişisel verileriniz, yukarıda belirtilen amaçların gerçekleştirilmesi doğrultusunda, hizmet aldığımız tedarikçiler ve iş ortaklarımız ile paylaşılabilir.
                </p>
                
                <p>
                  <strong>5. Kişisel Veri Toplamanın Yöntemi ve Hukuki Sebebi:</strong><br />
                  Kişisel verileriniz, elektronik ortamda form doldurma yoluyla toplanmaktadır. Veri işleme faaliyetinin hukuki sebepleri, açık rızanız ve meşru menfaattir.
                </p>
                
                <p>
                  <strong>6. Kişisel Veri Sahibinin Hakları:</strong><br />
                  6698 sayılı KVKK'nın 11. maddesi uyarınca sahip olduğunuz haklar:<br />
                  • Kişisel verilerinizin işlenip işlenmediğini öğrenme<br />
                  • Kişisel verileriniz işlenmişse buna ilişkin bilgi talep etme<br />
                  • Kişisel verilerinizin işlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme<br />
                  • Yurt içinde veya yurt dışında kişisel verilerinizin aktarıldığı üçüncü kişileri bilme<br />
                  • Kişisel verilerinizin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme<br />
                  • KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerinizin silinmesini veya yok edilmesini isteme<br />
                  • Kişisel verilerinizin aktarıldığı üçüncü kişilere yukarıda sayılan (e) ve (f) bentleri uyarınca yapılan işlemlerin bildirilmesini isteme<br />
                  • İşlenen verilerinizin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle kişiliğiniz aleyhine bir sonucun ortaya çıkmasına itiraz etme<br />
                  • Kişisel verilerinizin kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme
                </p>
                
                <p>
                  <strong>7. İletişim:</strong><br />
                  Yukarıda belirtilen haklarınızı kullanmak için info@shevastudiokonya.com adresinden bizimle iletişime geçebilirsiniz.
                </p>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowKvkkModal(false)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
            <div className="mb-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Randevunuz Başarıyla Oluşturuldu!
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Randevunuz başarıyla alındı.
              </p>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 md:py-12 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-xs sm:text-sm">
            © 2025 Sheva Studio. Tüm Hakları Saklıdır.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;



